import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { MailEntity } from '@adornis/mail-client/mail-entity.js';
import { MailProcessing } from '../features/information-extraction/db/MailExtractionResult.js';

/**
 * Project-specific subclass of MailEntity that carries the joined MailProcessing
 * array produced by MailboxFilter's $lookup stage.
 *
 * Uses the same `mail` collection as MailEntity — the `_class` field is only
 * rewritten in-memory by the dedicated subscription resolver, never persisted
 * to disk. This keeps the MailSyncService and all other mail write paths
 * untouched.
 */
@Entity()
export class EnrichedMailEntity extends MailEntity {
  static override _class = 'EnrichedMailEntity';
  static override _collectionName = MailEntity._collectionName;

  @Field(() => [MailProcessing])
  mailProcessings: Maybe<MailProcessing[]>;

  /**
   * Set when the mail has been archived (moved out of the INBOX into the IMAP
   * archive folder). Lives in the same `mail` collection; declared here rather
   * than on the engine's MailEntity so the archive feature stays app-local.
   * The overview hides archived mails unless explicitly requested.
   */
  @Field(() => Date)
  archivedAt: Maybe<Date>;

  /**
   * Set when this mail is an employee's answer to a question about another request — it then
   * carries that request's mailID. Such a mail is correspondence, not an inquiry, and the
   * overview hides it.
   *
   * Deliberately its own field rather than `archivedAt`: that one mirrors IMAP folder
   * membership, and `reconcileArchivedMails` would clear it again at the next boot because
   * the message is still sitting in the INBOX.
   */
  @Field(() => String)
  notificationReplyFor: Maybe<string>;

  /**
   * Sentences in a reply that answered none of the open questions. Recorded so they are not
   * silently swallowed: the sender wrote them, and "das hab ich doch geschrieben" needs an
   * answer other than a shrug. Scoped to the reply mail rather than to a notification, because
   * by definition they belong to no single question.
   */
  @Field(() => [String])
  notificationReplyUnmatched: Maybe<string[]>;

  /**
   * Set while this reply is being read, cleared when the reading is done.
   *
   * Reading an answer is a model call that takes seconds, and the mail is marked as a reply
   * before it starts — it must never fall through to the extraction. Without this marker the
   * Mailverkehr view showed the letter during that window as one that nothing came of, next to
   * a button offering to read it again. It is a timestamp rather than a flag so a reading that
   * died with the process (server restart mid-call) ages out instead of hanging there forever.
   */
  @Field(() => Date)
  notificationReplyReadingSince: Maybe<Date>;

  constructor(doc: PartialEntityData<EnrichedMailEntity>) {
    super(doc);
  }
}
